import { computed, defineComponent, h } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

// Import route guards and types
import { authGuard, titleGuard, permissionGuard } from './guards';
import { RouteUtils, ROUTE_NAMES } from './types';
import { CONSOLE_AGENT_ID } from '@/features/console/constants';

// Import layouts
import MainLayout from '@/layouts/MainLayout.vue';
import AuthLayout from '@/layouts/AuthLayout.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';

// Import pages
import LandingPage from '@/pages/LandingPage.vue';
import DashboardPage from '@/pages/DashboardPage.vue';
import NotFoundPage from '@/pages/NotFoundPage.vue';
import ErrorPage from '@/pages/ErrorPage.vue';
import TermsOfServicePage from '@/pages/TermsOfServicePage.vue';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage.vue';

// Import auth pages
import LoginPage from '@/features/auth/pages/LoginPage.vue';
import RegisterPage from '@/features/auth/pages/RegisterPage.vue';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage.vue';
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage.vue';
import VerifyEmailPage from '@/features/auth/pages/VerifyEmailPage.vue';

// Import feature pages
import UserProfilePage from '@/features/users/pages/UserProfilePage.vue';
import ApiKeysPage from '@/features/api-keys/pages/ApiKeysPage.vue';
import ModelGatewayPage from '@/features/model-gateway/pages/ModelGatewayPage.vue';
import McpGatewayPage from '@/features/mcp-gateway/pages/McpGatewayPage.vue';
import {
  OrganizationSettingsPage,
  AcceptInvitationPage,
  OrganizationsListPage,
  OrganizationCreditsPage,
} from '@/features/organizations/pages';
import PlaygroundPage from '@/features/playground/pages/PlaygroundPage.vue';
import McpPlayground from '@/features/playground/components/mcp/McpPlayground.vue';
import { useAuthStore } from '@/stores/auth';

const McpPlaygroundRoute = defineComponent({
  name: 'McpPlaygroundRoute',
  setup() {
    const authStore = useAuthStore();
    const authToken = computed(() => authStore.token);

    return () => {
      if (!authToken.value) {
        return h('div', { class: 'text-gray-600 dark:text-gray-400' }, 'Authentication required.');
      }

      return h(McpPlayground, { authMethod: 'bearer', authToken: authToken.value });
    };
  },
});

// Extend RouteRecordRaw to include typed meta
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiresGuest?: boolean;
    requiresVerification?: boolean;
    role?: string;
    permissions?: string[];
    title?: string;
    layout?: 'main' | 'auth' | 'public' | 'admin';
    showInNav?: boolean;
    navOrder?: number;
    navIcon?: string;
    disabled?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  // Public routes
  RouteUtils.createPublicRoute({
    path: '/',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: ROUTE_NAMES.LANDING,
        component: LandingPage,
        meta: {
          title: 'ASAB - Advanced Self-Aware Bots',
          description: 'Advanced Self-Aware Bots for intelligent automation',
          keywords: ['AI', 'bots', 'automation', 'neural networks'],
          showInNav: false,
        },
      },
      {
        path: 'terms-of-service',
        name: ROUTE_NAMES.TERMS,
        component: TermsOfServicePage,
        meta: {
          title: 'Terms of Service',
          description: 'Neural Network Agreement - Terms of Service',
          showInNav: false,
        },
      },
      {
        path: 'privacy-policy',
        name: ROUTE_NAMES.PRIVACY,
        component: PrivacyPolicyPage,
        meta: {
          title: 'Privacy Policy',
          description: 'Neural Data Protection Policy',
          showInNav: false,
        },
      },
      {
        path: 'error',
        name: ROUTE_NAMES.ERROR,
        component: ErrorPage,
        meta: {
          title: 'System Error',
          showInNav: false,
        },
      },
    ],
  }),

  // Auth routes
  RouteUtils.createAuthRoute({
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: ROUTE_NAMES.LOGIN,
        component: LoginPage,
        meta: {
          title: 'Sign In',
          description: 'Sign in to your neural control center',
        },
      },
      {
        path: 'register',
        name: ROUTE_NAMES.REGISTER,
        component: RegisterPage,
        meta: {
          title: 'Sign Up',
          description: 'Create your neural network account',
        },
      },
      {
        path: 'forgot-password',
        name: ROUTE_NAMES.FORGOT_PASSWORD,
        component: ForgotPasswordPage,
        meta: {
          title: 'Forgot Password',
          description: 'Reset your neural access credentials',
        },
      },
      {
        path: 'reset-password',
        name: ROUTE_NAMES.RESET_PASSWORD,
        component: ResetPasswordPage,
        meta: {
          title: 'Reset Password',
          description: 'Set new neural access credentials',
        },
      },
      {
        path: 'verify-email',
        name: ROUTE_NAMES.VERIFY_EMAIL,
        component: VerifyEmailPage,
        meta: {
          title: 'Verify Email',
          description: 'Verify your neural network connection',
          requiresGuest: false, // Allow both authenticated and guest users
        },
      },
    ],
  }),

  // Protected routes
  RouteUtils.createProtectedRoute({
    path: '/app',
    component: MainLayout,
    children: [
      {
        path: '',
        name: ROUTE_NAMES.DASHBOARD,
        component: DashboardPage,
        meta: {
          title: 'Neural Control Center',
          description: 'Your central neural command interface',
          showInNav: true,
          navOrder: 1,
          navIcon: 'dashboard',
        },
      },
      {
        path: 'profile',
        name: ROUTE_NAMES.PROFILE,
        component: UserProfilePage,
        meta: {
          title: 'Profile',
          description: 'Manage your neural profile settings',
          showInNav: true,
          navOrder: 2,
          navIcon: 'user',
        },
      },
      {
        path: 'api-keys',
        name: ROUTE_NAMES.API_KEYS,
        component: ApiKeysPage,
        meta: {
          title: 'API Keys',
          description: 'Manage your neural network access keys',
          showInNav: true,
          navOrder: 3,
          navIcon: 'key',
        },
      },
      {
        path: 'model-gateway',
        name: ROUTE_NAMES.MODEL_GATEWAY,
        component: ModelGatewayPage,
        meta: {
          title: 'Model Gateway',
          description: 'Manage LLM models and routing configurations',
          showInNav: true,
          navOrder: 4,
          navIcon: 'server',
        },
      },
      {
        path: 'mcp-gateway',
        name: ROUTE_NAMES.MCP_GATEWAY,
        component: McpGatewayPage,
        meta: {
          title: 'MCP Gateway',
          description: 'Manage MCP tools and marketplace access',
          showInNav: true,
          navOrder: 5,
          navIcon: 'server',
        },
      },
      {
        path: 'agents',
        name: ROUTE_NAMES.AGENTS,
        component: () => import('@/features/agents/pages/AgentListView.vue'),
        meta: {
          title: 'Agents',
          description: 'Manage and interact with AI agents',
          showInNav: true,
          navOrder: 5.5,
          navIcon: 'cpu-chip',
        },
      },
      {
        path: 'traces',
        name: ROUTE_NAMES.TRACES,
        component: () => import('@/features/traces/pages/TracesPage.vue'),
        meta: {
          title: 'Traces',
          description: 'Execution trace history',
          showInNav: true,
          navOrder: 5.55,
          navIcon: 'chart-bar',
        },
      },
      {
        path: 'memories',
        name: ROUTE_NAMES.MEMORIES,
        component: () => import('@/features/memories/pages/MemoriesPage.vue'),
        meta: {
          title: 'Memories',
          description: 'Org-shared agent memory library',
          showInNav: true,
          navOrder: 5.6,
          navIcon: 'book-open',
          permissions: ['agents:read'],
        },
      },
      {
        path: 'agents/:agentId/chat',
        name: ROUTE_NAMES.AGENT_CHAT,
        component: () => import('@/features/agents/pages/AgentChatView.vue'),
        meta: {
          title: 'Agent Chat',
          showInNav: false,
        },
        // The console agent's one tool (run_command) only functions on the
        // console surface, where the browser executes reads and reviews writes.
        // Opened in the generic agent chat it falls back to a paste-the-output
        // form the user cannot answer, so send it to the console instead —
        // carrying the conversation pointer so a reload-shared link still works.
        beforeEnter: (to) => {
          if (to.params.agentId === CONSOLE_AGENT_ID) {
            return { name: ROUTE_NAMES.CONSOLE, query: to.query };
          }
        },
      },
      {
        path: 'agents/kernel',
        name: ROUTE_NAMES.AGENT_KERNEL,
        component: () => import('@/features/agents/pages/KernelDashboardView.vue'),
        meta: {
          title: 'Agent Kernel',
          requiresAuth: true,
          requiresVerification: true,
          showInNav: false,
        },
      },
      {
        path: 'agents/workflows/new',
        name: ROUTE_NAMES.AGENT_WORKFLOW_BUILDER,
        component: () => import('@/features/agents/pages/WorkflowBuilderView.vue'),
        meta: {
          title: 'Workflow Builder',
          requiresAuth: true,
          requiresVerification: true,
          showInNav: false,
        },
      },
      {
        path: 'agents/workflows/templates',
        name: ROUTE_NAMES.AGENT_WORKFLOW_TEMPLATES,
        component: () => import('@/features/agents/pages/WorkflowTemplatesView.vue'),
        meta: {
          title: 'Workflow Templates',
          requiresAuth: true,
          requiresVerification: true,
          showInNav: false,
        },
      },
      {
        path: 'agents/workflows/:id',
        name: ROUTE_NAMES.AGENT_WORKFLOW_RUN,
        component: () => import('@/features/agents/pages/WorkflowRunView.vue'),
        meta: {
          title: 'Workflow Run',
          showInNav: false,
        },
      },
      {
        path: 'agents/workflows/:id/replay/:runId',
        name: ROUTE_NAMES.EXECUTION_REPLAY,
        component: () => import('@/features/agents/pages/ExecutionReplayView.vue'),
        meta: {
          title: 'Execution Replay',
          showInNav: false,
        },
      },
      {
        path: 'agents/:id/versions',
        name: ROUTE_NAMES.AGENT_VERSIONS,
        component: () => import('@/features/agents/pages/AgentVersionsView.vue'),
        meta: {
          title: 'Agent Versions',
          showInNav: false,
        },
      },
      {
        path: 'agents/analytics',
        name: ROUTE_NAMES.AGENT_ANALYTICS,
        component: () => import('@/features/agents/pages/CostDashboardView.vue'),
        meta: {
          title: 'Agent Analytics',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/messages',
        name: ROUTE_NAMES.KERNEL_MESSAGES,
        component: () => import('@/features/agents/pages/MessagesView.vue'),
        meta: {
          title: 'Kernel Messages',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/hooks',
        name: ROUTE_NAMES.KERNEL_HOOKS,
        component: () => import('@/features/agents/pages/HooksManagerView.vue'),
        meta: {
          title: 'Hooks Manager',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/events',
        name: ROUTE_NAMES.KERNEL_EVENTS,
        component: () => import('@/features/agents/pages/EventSubscriptionsView.vue'),
        meta: {
          // Outbound: the platform posts kernel events to a subscriber's URL.
          // Distinct from Event Triggers, which is the inbound direction.
          title: 'Outbound Webhooks',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/processes/:pid/memory',
        name: ROUTE_NAMES.KERNEL_PROCESS_MEMORY,
        component: () => import('@/features/agents/pages/MemoryInspectorView.vue'),
        meta: {
          title: 'Memory Inspector',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/processes/:pid/debug',
        name: ROUTE_NAMES.KERNEL_PROCESS_DEBUG,
        component: () => import('@/features/agents/pages/AgentDebuggerView.vue'),
        meta: {
          title: 'Agent Debugger',
          showInNav: false,
        },
      },
      {
        path: 'agents/kernel/handoffs/:agentId',
        name: ROUTE_NAMES.KERNEL_HANDOFF_TRACE,
        component: () => import('@/features/agents/pages/HandoffTraceView.vue'),
        meta: {
          title: 'Handoff Trace',
          showInNav: false,
        },
      },
      {
        path: 'marketplace',
        name: ROUTE_NAMES.MARKETPLACE,
        component: () => import('@/features/agents/pages/MarketplaceView.vue'),
        meta: {
          title: 'Marketplace',
          description: 'Browse and add community agents',
          showInNav: true,
          navOrder: 5.6,
          navIcon: 'shopping-bag',
        },
      },
      {
        path: 'approvals',
        name: ROUTE_NAMES.APPROVALS,
        component: () => import('@/features/agents/pages/ApprovalQueueView.vue'),
        meta: {
          title: 'Approvals',
          description: 'Pending tool use approvals',
          showInNav: true,
          navOrder: 5.7,
          navIcon: 'check-circle',
        },
      },
      {
        path: 'builds',
        name: ROUTE_NAMES.BUILDS,
        component: () => import('@/features/builds/pages/BuildsListPage.vue'),
        meta: {
          title: 'Builds',
          description: 'Custom tool and hook build history',
          showInNav: true,
          navOrder: 5.75,
          navIcon: 'cube',
        },
      },
      {
        path: 'builds/:kind/:buildId',
        name: ROUTE_NAMES.BUILD_DETAIL,
        component: () => import('@/features/builds/pages/BuildDetailPage.vue'),
        meta: { title: 'Build Detail', showInNav: false },
      },
      {
        // The console is a chat surface, so it needs no permission of its own:
        // every command it runs goes through the session's own credential and is
        // authorized there. Gating the page would only hide a surface that
        // cannot exceed what the user already holds.
        path: 'console',
        name: ROUTE_NAMES.CONSOLE,
        component: () => import('@/features/console/pages/ConsolePage.vue'),
        meta: { title: 'Console' },
      },
      {
        path: 'live/:taskId',
        name: ROUTE_NAMES.LIVE_EXECUTION,
        component: () => import('@/features/live-execution/pages/LiveExecutionPage.vue'),
        meta: {
          title: 'Live Execution',
          showInNav: false,
          permissions: ['executions:read'],
        },
      },
      {
        path: 'skills',
        name: ROUTE_NAMES.SKILLS,
        component: () => import('@/features/skills/pages/SkillsPage.vue'),
        meta: {
          title: 'Skills',
          description: 'Org-shared agent skill packs',
          showInNav: true,
          navOrder: 5.8,
          navIcon: 'academic-cap',
          permissions: ['skills:read'],
        },
      },
      {
        path: 'guardrails',
        name: ROUTE_NAMES.GUARDRAILS,
        component: () => import('@/features/guardrails/pages/GuardrailsPage.vue'),
        meta: {
          title: 'Guardrails',
          description: 'Org-shared agent safety policies',
          showInNav: true,
          navOrder: 5.85,
          navIcon: 'shield-check',
          permissions: ['guardrails:read'],
        },
      },
      {
        path: 'event-triggers',
        name: ROUTE_NAMES.EVENT_TRIGGERS,
        component: () => import('@/features/event-triggers/pages/EventTriggersPage.vue'),
        meta: {
          title: 'Event Triggers',
          description: 'Run an agent when a matching event arrives',
          showInNav: true,
          navOrder: 5.87,
          navIcon: 'bolt',
          // Deliberately ungated. The endpoints carry no RequirePermission —
          // triggers are owner-scoped and ownership is the whole gate — so any
          // authenticated user can create and manage their own. Adding a
          // permission here would invent a restriction the API does not have and
          // could lock an owner out of triggers they already have.
        },
      },
      {
        path: 'integrations',
        name: ROUTE_NAMES.INTEGRATIONS,
        component: () => import('@/features/integrations/pages/IntegrationsPage.vue'),
        meta: {
          title: 'Integrations',
          description: 'Connect external MCP servers and manage connections',
          showInNav: true,
          navOrder: 5.9,
          navIcon: 'puzzle-piece',
          permissions: ['connections:read'],
        },
      },
      {
        path: 'playground',
        name: ROUTE_NAMES.PLAYGROUND,
        component: PlaygroundPage,
        meta: {
          title: 'API Playground',
          description: 'Test chat and MCP APIs interactively',
          showInNav: true,
          navOrder: 6,
          navIcon: 'beaker',
        },
      },
      {
        path: 'mcp-playground',
        name: ROUTE_NAMES.MCP_PLAYGROUND,
        component: McpPlaygroundRoute,
        meta: {
          title: 'MCP Playground',
          description: 'Inspect and run MCP tools interactively',
          showInNav: true,
          navOrder: 7,
          navIcon: 'robot',
        },
      },
      {
        path: 'organizations',
        name: ROUTE_NAMES.ORGANIZATIONS,
        component: OrganizationsListPage,
        meta: {
          title: 'Organizations',
          description: 'Manage your organizations and teams',
          showInNav: true,
          navOrder: 8,
          navIcon: 'building-office',
        },
      },
      {
        path: 'organizations/settings',
        name: ROUTE_NAMES.ORGANIZATION_SETTINGS,
        component: OrganizationSettingsPage,
        meta: {
          title: 'Organization Settings',
          description: 'Manage your organization settings',
          showInNav: false,
        },
      },
      {
        path: 'organizations/:id/credits',
        name: ROUTE_NAMES.ORGANIZATION_CREDITS,
        component: OrganizationCreditsPage,
        meta: {
          title: 'Buy Credits',
          description: 'Top up your organization credits',
          showInNav: false,
        },
      },
      {
        path: 'organizations/:id/billing',
        name: ROUTE_NAMES.ORGANIZATION_BILLING,
        component: () => import('@/features/billing/pages/BillingPage.vue'),
        meta: {
          title: 'Billing',
          description: 'Manage subscription, seats, cores, invoices',
          showInNav: false,
        },
      },
      {
        path: 'invitations/:token',
        name: ROUTE_NAMES.ACCEPT_INVITATION,
        component: AcceptInvitationPage,
        meta: {
          title: 'Accept Invitation',
          description: 'Join an organization',
          showInNav: false,
        },
      },
      {
        path: 'mcp-gateway/custom-tools/:id/builds',
        name: ROUTE_NAMES.CUSTOM_TOOL_BUILDS,
        component: () => import('@/features/mcp-gateway/pages/CustomToolBuildsPage.vue'),
        meta: { title: 'Tool Builds', showInNav: false },
      },
      {
        path: 'mcp-gateway/custom-tools/:id/builds/new',
        name: ROUTE_NAMES.CUSTOM_TOOL_NEW_BUILD,
        component: () => import('@/features/mcp-gateway/pages/CustomToolNewBuildPage.vue'),
        meta: { title: 'New Build', showInNav: false },
      },
      {
        path: 'mcp-gateway/custom-tools/:id/builds/:buildId',
        name: ROUTE_NAMES.CUSTOM_TOOL_BUILD_DETAIL,
        component: () => import('@/features/mcp-gateway/pages/CustomToolBuildDetailPage.vue'),
        meta: { title: 'Build Detail', showInNav: false },
      },
      {
        path: 'mcp-gateway/custom-tools/:id/versions',
        name: ROUTE_NAMES.CUSTOM_TOOL_VERSIONS,
        component: () => import('@/features/mcp-gateway/pages/CustomToolVersionsPage.vue'),
        meta: { title: 'Tool Versions', showInNav: false },
      },
      {
        path: 'documentation',
        name: ROUTE_NAMES.DOCUMENTATION,
        component: () => import('@/pages/DocumentationPage.vue'),
        meta: {
          title: 'API Documentation',
          description: 'Complete guide to using our API',
          showInNav: true,
          navOrder: 9,
          navIcon: 'book',
        },
      },
    ],
  }),

  // Catch-all 404 route
  {
    path: '/:pathMatch(.*)*',
    name: ROUTE_NAMES.NOT_FOUND,
    component: NotFoundPage,
    meta: {
      title: 'Page Not Found',
      layout: 'public',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Configure router options
  scrollBehavior(to, from, savedPosition) {
    // If we have a saved position (from browser back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }

    // If there's a hash, scroll to that element
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }

    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' };
  },
});

// Global navigation guards setup
router.beforeEach(authGuard);
router.beforeEach(permissionGuard);
router.beforeEach(titleGuard);

// Global after guard for analytics and cleanup
router.afterEach((to, from) => {
  // Track page views if enabled
  if (to.meta?.trackPageView) {
    // Add your analytics tracking here
    console.log(`Page view: ${to.path}`);
  }

  // Remove loading states
  const loadingElement = document.querySelector('.route-loading');
  if (loadingElement) {
    loadingElement.remove();
  }

  // Log navigation for debugging
  if (import.meta.env.DEV) {
    console.log(`✅ Navigated from ${from.path} to ${to.path}`);
  }
});

// Navigation error handler
router.onError((error, to, from) => {
  console.error('Router error:', error);

  // Try to recover from routing errors
  if (from) {
    router.push({ name: ROUTE_NAMES.ERROR });
  } else {
    router.push({ name: ROUTE_NAMES.LANDING });
  }
});

export default router;
